import { useRef, useState } from "react";
import { Camera, RefreshCw, Loader2 } from "lucide-react";
import { toast } from "sonner";

async function compressImage(file, maxDim = 640, quality = 0.82) {
    const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
    const img = await new Promise((resolve, reject) => {
        const i = new Image();
        i.onload = () => resolve(i);
        i.onerror = reject;
        i.src = dataUrl;
    });
    const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(img.width * scale);
    canvas.height = Math.round(img.height * scale);
    canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL("image/jpeg", quality);
}

/**
 * Iter-34 — Capture photo anti-fraude pour l'auto-inscription.
 * Mobile : ouvre directement la caméra frontale (capture="user").
 * Desktop : sélecteur de fichier classique. Compression canvas → JPEG ~640px.
 */
export default function PhotoCapture({ value, onChange }) {
    const inputRef = useRef(null);
    const [busy, setBusy] = useState(false);

    const handleFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!file.type.startsWith("image/")) {
            toast.error("Choisis une image (photo).");
            return;
        }
        setBusy(true);
        try {
            onChange(await compressImage(file));
        } catch {
            toast.error("Impossible de lire cette image. Réessaie.");
        } finally {
            setBusy(false);
            e.target.value = "";
        }
    };

    return (
        <div
            className="glass-card rounded-2xl p-4 flex items-center gap-4"
            data-testid="photo-capture"
        >
            <input
                ref={inputRef}
                type="file"
                accept="image/*"
                capture="user"
                onChange={handleFile}
                className="hidden"
                data-testid="photo-capture-input"
            />
            {value ? (
                <img
                    src={value}
                    alt="Ta photo"
                    data-testid="photo-capture-preview"
                    className="w-16 h-16 rounded-2xl object-cover border-2 border-shariti-orange/50 shrink-0"
                />
            ) : (
                <div className="w-16 h-16 rounded-2xl bg-shariti-orange/15 border-2 border-dashed border-shariti-orange/40 flex items-center justify-center shrink-0">
                    {busy ? (
                        <Loader2 className="w-6 h-6 text-shariti-orange animate-spin" />
                    ) : (
                        <Camera className="w-6 h-6 text-shariti-orange" />
                    )}
                </div>
            )}
            <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-white">
                    {value ? "Photo ajoutée ✓" : "Ta photo"}
                </p>
                <p className="text-xs text-white/50">
                    Une photo de toi, visage visible — elle permet à l'équipe de vérifier
                    ton inscription.
                </p>
            </div>
            <button
                type="button"
                onClick={() => inputRef.current?.click()}
                disabled={busy}
                data-testid="photo-capture-button"
                className="inline-flex items-center gap-1.5 bg-shariti-orange/20 hover:bg-shariti-orange/30 border border-shariti-orange/40 text-shariti-orange-light font-extrabold text-xs uppercase tracking-widest px-3 py-2 rounded-xl transition-all shrink-0 disabled:opacity-60"
            >
                {value ? <RefreshCw className="w-3.5 h-3.5" /> : <Camera className="w-3.5 h-3.5" />}
                {value ? "Changer" : "Ajouter"}
            </button>
        </div>
    );
}
